import React from "react"
import { graphql, Link } from "gatsby"
import styled from "styled-components"
import Img from "gatsby-image"

import { Section, Container } from "@atoms/layout"
import { Heading } from "@atoms/typography"
import { media } from "@utils/media"
import CardPost from "@molecules/cards/post"

import SEO from "@seo/seo"
import Layout from "@utils/layout"

const Christmas = ({ data }) => {
  const { header, gift, wallflower, body } = data.craft

  return (
    <Layout>
      <SEO title="Christmas 2021 Gift Guides" />

      <Section xlTop lg>
        <Container containerMedium>
          <IntroImg fluid={data.intro.childImageSharp.fluid} />
          <Heading html="h1" level="h2" center>
            A slow and thoughtful approach to the holiday season
          </Heading>
        </Container>
      </Section>

      <Section lg>
        <Featured grid>
          <FeaturedPosts grid>
            {header.map((post, index) => (
              <CardPost post={post} key={index} />
            ))}
          </FeaturedPosts>

          <Sidebar to="/a-thoughtful-guide-to-gifts">
            <Image fluid={data.gift.childImageSharp.fluid} />
            <Heading level="h3" html="h3" center>
              A Thoughtful Guide to Gifts
            </Heading>
            <Heading html="h6" level="sh" center noMargin>
              Read More
            </Heading>
          </Sidebar>
        </Featured>
      </Section>

      <WallflowerLink
        target="_blank"
        href="https://www.etsy.com/shop/GetWallflower?ref=simple-shop-header-name&listing_id=777748065"
      >
        <Section md>
          <Container bg flex alignCenter justifyCenter lg lgTop>
            <Background>
              <Img fluid={data.wallflower.childImageSharp.fluid} />
            </Background>
            <WallflowerContent>
              <Heading html="h2" level="h2" noMargin light center>
                Wallflower Holiday Collection
              </Heading>
            </WallflowerContent>
          </Container>
        </Section>
      </WallflowerLink>

      <Section md mdTop>
        <Posts grid>
          {body.map((post, index) => (
            <CardPost post={post} key={index} />
          ))}
        </Posts>
      </Section>
    </Layout>
  )
}

export default Christmas

const IntroImg = styled(Img)`
  max-width: 10rem;
  margin: 0 auto;
  margin-bottom: 2rem;
`

const Featured = styled(Container)`
  grid-template-columns: 1fr;

  ${media.sm`
    grid-template-columns: 2fr 1fr;
  `}
`

const Image = styled(Img)`
  margin-bottom: var(--spacingXSmall);
  flex: 1 0 60%;
  transition: var(--transitionLong);

  img {
    transition: var(--transitionLong) !important;
  }
`

const FeaturedPosts = styled(Container)`
  grid-template-columns: 1fr;
  grid-auto-rows: minmax(min-content, max-content);

  ${media.sm`
    grid-template-columns: 1fr 1fr;
  `}

  article:nth-of-type(1) {
    ${media.sm`
      grid-area: 1 / 1 / 2 / 3;
    `}
  }
  article:nth-of-type(2) {
    ${media.sm`
      grid-area: 2 / 1 / 3 / 1;
    `}
  }
  article:nth-of-type(3) {
    ${media.sm`
      grid-area: 2 / 2 / 3 / 2;
    `}
  }
`

const Posts = styled(Container)`
  grid-template-columns: 1fr;

  ${media.sm`
    grid-template-columns: 1fr 1fr 1fr;
  `}
`

const Sidebar = styled(Link)`
  align-items: center;
  background-color: var(--red20);
  display: flex;
  flex-direction: column;
  padding: var(--spacingSmall);
  height: calc(100vh - 8rem);

  ${media.sm`
    align-self: start;
    grid-column: 2;
    grid-row: 1;
    left: 0;
    position: sticky;
    top: 5rem;
  `}
`

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;

  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
    width: 100%;
    height: 100%;
  }
`

const WallflowerContent = styled.div`
  z-index: 1;
  text-align: center;
`

const WallflowerLink = styled.a`
  z-index: 1;
`

export const fetchChristmasCategory = graphql`
  query fetchChristmasCategory {
    intro: file(relativePath: { eq: "christmas/presents.png" }) {
      id
      childImageSharp {
        fluid(maxWidth: 400) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    gift: file(relativePath: { eq: "christmas/sidebar.png" }) {
      id
      childImageSharp {
        fluid(maxWidth: 640) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    wallflower: file(relativePath: { eq: "christmas/wallflower.jpg" }) {
      id
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    craft {
      category(id: 172834) {
        ... on craft_postCategories_Category {
          title
          id
          uri
          slug
          metaDescription
          metaTags {
            title
          }
          metaImage {
            url
          }
          children {
            title
            uri
          }
          parent {
            title
            uri
            children {
              title
              uri
            }
          }
          displayTitle
          featuredContent {
            url
            title
          }
          featuredContentDisplay
        }
      }
      header: entries(categories: 172834, offset: 0, limit: 3) {
        ...CardPost
      }
      body: entries(categories: 172834, offset: 3) {
        ...CardPost
      }
    }
  }
`
